import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type ViewMode = 'grid' | 'list';

type ViewModeContextType = {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
};

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

export const ViewModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [viewMode, setViewModeState] = useState<ViewMode>('grid');

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('global_view_mode');
      if (saved === 'grid' || saved === 'list') {
        setViewModeState(saved);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem('global_view_mode', mode);
    } catch {
      // ignore storage errors
    }
  };

  const toggleViewMode = () => setViewMode(viewMode === 'grid' ? 'list' : 'grid');

  const value = useMemo(() => ({ viewMode, setViewMode, toggleViewMode }), [viewMode]);

  return (
    <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>
  );
};

export const useViewMode = (): ViewModeContextType => {
  const ctx = useContext(ViewModeContext);
  if (!ctx) throw new Error('useViewMode must be used within a ViewModeProvider');
  return ctx;
};
